import { CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { buildFormSchema, buildInitialData } from "@/components/templates/ProductForm/form-data";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetProductById from "@/hooks/requests/product/use-get-product-by-id";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";
import { ProductFormStateProvider } from "@/contexts/product-form-state-context";
import { useGetLinkedCategories } from "@/hooks/requests/linked-product-category";

import { ProductFormData } from "./types";
import { FormWrapper } from "./FormWrapper";

type Props = {
  messages: ProductFormPageMessages;
  isEditing?: boolean;
};

export default function ProductFormPage({ messages, isEditing = false }: Props) {
  const { productId } = useParams<{ productId: string }>();
  const { lang } = useLanguage();
  const _id = Number(productId) || 0;
  const customerId = useGetCustomerId();
  const formatCustomerId = Number(customerId) || 0;
  const { data: product, isLoading: isLoadingProduct, error } = useGetProductById({ _id });
  const { data: linkedCategories } = useGetLinkedCategories({ productId: product?.id || null });
  
  const selectedCategoryIds = linkedCategories?.map(lc => lc.categoryId) || [];

  const handleSubmit = async (_formData: ProductFormData) => {
    // This is just a placeholder - actual submission is handled in FormWrapper
  };

  const BREAD_CRUMBS = [{ title: messages.breadCrumbTitle, href: ROUTE_URLS.products(lang) }];

  const pageTitle = isEditing ? messages.editTitle : messages.createTitle;
  const shouldLoadError = !product && !!error && !isLoadingProduct;
  const shouldLoadForm = !!isLoadingProduct && !!customerId;

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={shouldLoadForm}>
          <Formik
            initialValues={buildInitialData(formatCustomerId, product, isEditing, selectedCategoryIds)}
            validationSchema={buildFormSchema(messages)}
            validateOnChange={false}
            enableReinitialize={false}
            onSubmit={handleSubmit}
          >
            {() => (
              <ProductFormStateProvider
                messages={messages}
                customerId={formatCustomerId}
                isEditing={isEditing}
              >
                <FormWrapper 
                  messages={messages} 
                  product={product} 
                  customerId={customerId}
                  isEditing={isEditing}
                />
              </ProductFormStateProvider>
            )}
          </Formik>

          {shouldLoadError && (
            <AlertBlock
              title={messages.notFoundError}
              description={error.message}
              alt={messages.notFoundAlt}
            />
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}
